// ./src/utils/api/PollAnswerService.ts
//
// Guest answers to poll/quiz questions. OpResult-native, event-scoped. Talks to the
// eventPollAnswer* GraphQL operations. The primary write is `submit` — a batch "answer on
// behalf": one or more guests answer the SAME set of questions in one call. The server
// validates each answer ONCE against its question (the answers are shared across guests, so
// a bad answer is a question-level error, reported under `answers[i].value`) and upserts one
// active answer per (guest, question).
//
// Host/crew (account users) enter these today; the SAME operation backs the guest's own
// answer on the public event website later (enteredByKind='guest'). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the server POLL_ANSWER_ENTERED_BY_KIND boundary)
// =============================================================================

export const POLL_ANSWER_ENTERED_BY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VISITOR: "visitor",
} as const;

export type PollAnswerEnteredByKind = (typeof POLL_ANSWER_ENTERED_BY_KIND)[keyof typeof POLL_ANSWER_ENTERED_BY_KIND];

// =============================================================================
// GraphQL
// =============================================================================

const ANSWER_FIELDS = `
  id
  eventId
  accountId
  pollQuestionId
  eventGuestId
  selectedOptionIds
  answerText
  answeredAt
  enteredByKind
  status
  rights
  createdAt
  updatedAt
`;

const ANSWER_LIST_QUERY = gql`
  query EventPollAnswerList($filter: EventPollAnswerFilter, $params: PaginationAndSorting) {
    eventPollAnswerList(filter: $filter, params: $params) {
      code
      data { ${ANSWER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ANSWER_SUBMIT_MUTATION = gql`
  mutation EventPollAnswerSubmit($params: EventPollAnswerSubmitInput) {
    eventPollAnswerSubmit(params: $params) {
      code
      total
      data { ${ANSWER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventPollAnswer schema)
// =============================================================================

export interface PollAnswer {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  pollQuestionId?: string | null;
  eventGuestId?: string | null;
  selectedOptionIds?: string[] | null;
  answerText?: string | null;
  answeredAt?: string | null;
  /** Provenance: user | crew | guest | visitor. */
  enteredByKind?: string | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

/** One question's shared answer in a batch submit (the same value for every selected guest). */
export interface PollAnswerValueInput {
  pollQuestionId: string;
  selectedOptionIds?: string[] | null;
  answerText?: string | null;
}

export interface PollAnswerSubmitInput {
  eventId: string;
  eventGuestIds: string[];
  answers: PollAnswerValueInput[];
  enteredByKind?: string | null;
}

export interface PollAnswerFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  pollQuestionId?: string[] | null;
  eventGuestId?: string[] | null;
  status?: number[] | null;
}

// =============================================================================
// Service
// =============================================================================

export class PollAnswerService {
  static async list(filter?: PollAnswerFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(ANSWER_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Answer a set of questions on behalf of one or more guests. Validation errors come back
   * question-scoped under `answers[i].value` (read with getIndexedFormErrors) so the drawer
   * can show each on its question. Anything else is generic.
   */
  static async submit(params: PollAnswerSubmitInput): Promise<OpResult> {
    return ApiService.executeMutation(ANSWER_SUBMIT_MUTATION, { params });
  }
}
